import React from "react";
import { Typography, Box } from "@mui/material";

const ReadingPatternsGuide = () => {
  return (
    <>
      <Typography variant="h6" className="section-heading" color="secondary">
        Pattern Structure
      </Typography>
      <Typography variant="body1">
        Most written knitting patterns follow a standard structure:
      </Typography>
      <ul className="feature-list">
        <li>
          <span className="feature-item-title">Title and Description</span>
          <span className="feature-item-description">
            The pattern name and a brief description of the finished item.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Materials</span>
          <span className="feature-item-description">
            Yarn type, weight, and amount needed. Needle sizes and types. Notions like stitch
            markers, cable needles, or buttons.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Gauge</span>
          <span className="feature-item-description">
            The number of stitches and rows per inch (or centimeter) you need to achieve. This is
            crucial for getting the right size!
          </span>
        </li>
        <li>
          <span className="feature-item-title">Size Information</span>
          <span className="feature-item-description">
            Finished measurements and how to adjust for different sizes.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Instructions</span>
          <span className="feature-item-description">
            Step-by-step directions, usually organized by section (cuff, body, decreases, etc.) or
            by row numbers.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Finishing</span>
          <span className="feature-item-description">
            Instructions for seaming, blocking, adding buttons, or other finishing touches.
          </span>
        </li>
      </ul>

      <Typography variant="h6" className="section-heading" color="secondary">
        Common Abbreviations
      </Typography>
      <Typography variant="body1">
        Written patterns use abbreviations to save space. Here are the most common:
      </Typography>
      <ul className="feature-list">
        <li>K = Knit</li>
        <li>P = Purl</li>
        <li>YO = Yarn Over</li>
        <li>K2Tog = Knit 2 Together (right-leaning decrease)</li>
        <li>SSK = Slip Slip Knit (left-leaning decrease)</li>
        <li>M1L = Make 1 Left (increase that slants left)</li>
        <li>M1R = Make 1 Right (increase that slants right)</li>
        <li>C2R = Cable 2 Right</li>
        <li>C2L = Cable 2 Left</li>
        <li>Rep = Repeat</li>
        <li>RS = Right Side</li>
        <li>WS = Wrong Side</li>
      </ul>
      <Typography variant="body2" color="text.secondary">
        Patterns should include a complete abbreviation list. If yours doesn't, refer to Craft Yarn
        Council standards or ask for clarification.
      </Typography>

      <Typography variant="h6" className="section-heading" color="secondary">
        Understanding Pattern Instructions
      </Typography>
      <Typography variant="body1">
        Pattern instructions use specific conventions:
      </Typography>
      <ul className="feature-list">
        <li>
          <span className="feature-item-title">Asterisks (*)</span>
          <span className="feature-item-description">
            Indicate the start of a repeat. For example, '*K2, P2; rep from *' means work K2, P2,
            then repeat that sequence.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Parentheses ()</span>
          <span className="feature-item-description">
            Often indicate sizes or optional instructions. For example, '(K2, P2) 3 times' means
            work K2, P2 three times total.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Brackets []</span>
          <span className="feature-item-description">
            Usually show stitch counts or indicate sections worked a certain number of times.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Semicolons (;)</span>
          <span className="feature-item-description">
            Separate different parts of an instruction, often marking the end of a repeat section.
          </span>
        </li>
      </ul>

      <Typography variant="h6" className="section-heading" color="secondary">
        Row-by-Row Instructions
      </Typography>
      <Typography variant="body1">
        Many patterns give instructions row by row:
      </Typography>
      <Box
        component="pre"
        className="pattern-example"
        sx={{
          fontFamily: "Roboto Mono, monospace",
          backgroundColor: "var(--lp-cream)",
          padding: "1rem",
          borderRadius: "8px",
          overflowX: "auto",
        }}
      >
        {`Row 1 (RS): K2, *YO, K2Tog, K2; rep from * to end.
Row 2 (WS): Purl all stitches.
Row 3 (RS): K1, *YO, K2Tog, K2; rep from * to last st, K1.
Row 4 (WS): Purl all stitches.`}
      </Box>
      <Typography variant="body2" color="text.secondary">
        This tells you exactly what to do on each row. RS means right side, WS means wrong side.
      </Typography>

      <Typography variant="h6" className="section-heading" color="secondary">
        Gauge and Swatching
      </Typography>
      <Typography variant="body1">
        Gauge is critical for getting the right size:
      </Typography>
      <ul className="feature-list">
        <li>
          <span className="feature-item-title">What is Gauge?</span>
          <span className="feature-item-description">
            Gauge is the number of stitches and rows per 4 inches (or sometimes per inch) that you
            get with a specific yarn and needle size. Most patterns specify gauge over 4 inches for
            accuracy.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Why It Matters</span>
          <span className="feature-item-description">
            If your gauge doesn't match the pattern, your finished item will be the wrong size. Too
            many stitches per inch = too small. Too few = too large.
          </span>
        </li>
        <li>
          <span className="feature-item-title">Making a Gauge Swatch</span>
          <span className="feature-item-description">
            Knit a swatch larger than 4×4 inches (at least 6×6 inches recommended) using the
            pattern's specified yarn and needles. Block the swatch as you'll block the finished
            item, then measure stitches and rows over the center 4 inches. Adjust needle size if
            needed.
          </span>
        </li>
      </ul>
    </>
  );
};

export default ReadingPatternsGuide;

